// Creating a user is a two-step write: first the Auth0 login (`POST /authUsers`),
// then the DB record (`POST /users`). If the second step fails the login already
// exists, so `useCreateUser` attempts to roll it back and raises this error to
// describe the resulting state. `authRolledBack` tells the UI whether the orphaned
// login was successfully removed; when it wasn't, `orphanedAuth0Id` carries the id
// an administrator needs to clean it up manually.
export class UserCreationError extends Error {
  readonly authRolledBack: boolean;
  readonly orphanedAuth0Id?: string;

  constructor(message: string, authRolledBack: boolean, orphanedAuth0Id?: string) {
    super(message);
    this.name = 'UserCreationError';
    this.authRolledBack = authRolledBack;
    this.orphanedAuth0Id = orphanedAuth0Id;
    // Restore the prototype chain so `instanceof` works when this is transpiled
    // to a target below ES2015 (TS extends-Error limitation).
    Object.setPrototypeOf(this, UserCreationError.prototype);
  }
}

// Maps any error thrown while creating a user to a user-facing message. The
// partial-failure case (login created, record not saved, rollback failed) gets
// an explicit call to action so the failure is never silent.
export const getCreateUserErrorMessage = (error: unknown): string => {
  if (error instanceof UserCreationError) {
    if (error.authRolledBack) {
      return "Couldn't create the user. No changes were saved — please try again.";
    }
    return `The login account was created but the user record couldn't be saved, leaving a partial account${
      error.orphanedAuth0Id ? ` (${error.orphanedAuth0Id})` : ''
    }. Please contact an administrator to remove it before retrying.`;
  }

  return error instanceof Error && error.message
    ? error.message
    : 'Something went wrong while creating the user. Please try again.';
};
